import React, { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Badge } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTruckContainer } from '@fortawesome/pro-duotone-svg-icons'
import { useFetch } from '../../../../components/component-items/helpers'
import CardLoading from '../../../../components/component-items/loading-popover'

// Components
import { OrderOptions } from '../../../orders/order/styles'
import { ShippingWorkflow } from '../shipping-workflow'
import GlobalStyles from '../../../../components/component-items/styles'

// Consolidated shipment form
import { ShipmentManagementForm } from '../../../orders/order/shipment-details'

// Cards
import { StatusBar } from '../status-bar'

/**
 * Full-page Shipment Details component used in the packing workflow.
 * Uses the consolidated ShipmentManagementForm in standalone mode.
 */
export const PackingShipmentDetails = () => {
  const order = useParams<{ id: string }>().id || ''

  const [refetchKey, setRefetchKey] = useState(0)
  const url = `/api/order/${order}/status/${refetchKey ? `?_=${refetchKey}` : ''}`
  const { response: resp, loaded, error }: any = useFetch(url, {})

  const handlingUnitDetails = loaded ? resp.handlingUnitDetails || [] : []
  const serviceLevels = loaded ? resp.serviceLevels || [] : []
  const shipment = loaded ? resp.shipment || null : null
  const b2b = loaded ? resp.b_to_b || false : false
  const shipBefore = loaded ? resp.ship_before || null : null
  const shipAfter = loaded ? resp.ship_after || null : null
  const statusId = loaded ? resp.orderStatus?.statusId : undefined
  const [packStatus, setPackStatus] = useState({
    fully_packed: false,
    needs_serial_numbers: false,
    serial_number_enabled: false,
    needs_dimensions: false,
    freight: true,
    transport_mode: 'ltl' as const,
  })

  const submitRef = useRef<(() => Promise<void>) | null>(null)
  const [saving, setSaving] = useState(false)

  // Auto-save callback: submit form before navigating away
  const handleBeforeNavigate = async () => {
    if (submitRef.current) {
      await submitRef.current()
    }
  }

  useEffect(() => {
    if (loaded) {
      setPackStatus(resp.packStatus)
    }
  }, [loaded, resp])

  const handleSuccess = () => {
    setRefetchKey((prev) => prev + 1)
  }

  return (
    <>
      <div className="row">
        <div className="col">
          <StatusBar />
        </div>
      </div>
      <div className="row">
        <div className="col-lg-3 col-xl-2">
          <OrderOptions>
            <ul>
              <GlobalStyles.Tooltip data-title="Save shipment details">
                <li
                  onClick={(e) => {
                    e.preventDefault()
                    if (!saving) submitRef.current?.()
                  }}
                  className={saving ? 'disabled' : ''}
                >
                  <a href="#" onClick={(e) => e.preventDefault()}>
                    <span className="icon">
                      <FontAwesomeIcon icon={faTruckContainer} />
                    </span>
                    <span className="text">{saving ? 'Saving...' : 'Update Shipment'}</span>
                  </a>
                </li>
              </GlobalStyles.Tooltip>
            </ul>
          </OrderOptions>
        </div>
        <div className="col-lg-7 col-xl-8">
          <div className="row">
            <div className="col-xl-12">
              <GlobalStyles.FullPageCard style={{ padding: '20px 10px', minHeight: 300 }}>
                <GlobalStyles.CardHeader>
                  <GlobalStyles.CardTitle>
                    <h3>
                      Shipment Details
                      {shipment?.shipment_id && (
                        <Badge
                          bg="secondary"
                          style={{
                            fontSize: '0.65rem',
                            fontWeight: 500,
                            marginLeft: '0.75rem',
                            verticalAlign: 'middle',
                          }}
                        >
                          {shipment.shipment_id}
                        </Badge>
                      )}
                    </h3>
                  </GlobalStyles.CardTitle>
                </GlobalStyles.CardHeader>
                {loaded ? (
                  <div style={{ padding: '15px 15px 0' }}>
                    <ShipmentManagementForm
                      shipment={shipment}
                      handlingUnitDetails={handlingUnitDetails}
                      serviceLevels={serviceLevels}
                      orderId={order}
                      onSubmitSuccess={handleSuccess}
                      b2b={b2b}
                      shipBefore={shipBefore}
                      shipAfter={shipAfter}
                      statusId={statusId}
                      standalone
                      hideSubmitButton
                      submitRef={submitRef}
                      onLoadingChange={setSaving}
                    />
                  </div>
                ) : (
                  <CardLoading error={error} />
                )}
              </GlobalStyles.FullPageCard>
            </div>
          </div>
        </div>
        <div className="col-xl-2">
          <ShippingWorkflow {...packStatus} current={'shipment-details'} onBeforeNavigate={handleBeforeNavigate} />
        </div>
      </div>
    </>
  )
}
